import { createRouter, createWebHistory } from 'vue-router'
import { isLogin, checkRoleAccess, getUserProfile } from '@/utils/auth'
import { isStaleChunkError } from '@/utils/chunk-reload'
import Login from '@/components/pages/Login'
import Dashboard from '@/components/pages/admin/dashboard/index'
import AdminLayout from '@/components/layouts/Admin'
import EmptyPageLayout from '@/components/layouts/EmptyPage'
import sidebar from '@/components/globals/sidebar/sidebar.json'

const routes = [
  // EMPLOYEE DASHBOARD //
  {
    path: '/employee-dashboard',
    component: AdminLayout,
    redirect: '/employee-dashboard/employee-information',
    children: [
      {
        path: 'employee-information',
        name: 'EmployeeInformation',
        component: () => import(/* webpackChunkName: "employee-information" */ '@/components/pages/employee/employee-information/index'),
        meta: {
          menu: 'Informasi Data Karyawan',
          type: 'view'
        }
      },
      {
        path: 'employee-balance-information',
        name: 'EmployeeBalanceInformation',
        component: () => import(/* webpackChunkName: "employee-balance-information" */ '@/components/pages/employee/employee-balance-information/index'),
        meta: {
          menu: 'Informasi Saldo Karyawan',
          type: 'view'
        }
      },
      {
        path: 'employee-update-history',
        name: 'EmployeeUpdateHistory',
        component: () => import(/* webpackChunkName: "employee-update-history" */ '@/components/pages/employee/employee-update-history/index'),
        meta: {
          menu: 'Riwayat Pengkinian Data',
          type: 'view'
        }
      },
      {
        path: 'employee-update-history/:id/detail',
        name: 'EmployeeUpdateHistoryDetail',
        component: () => import( /* webpackChunkName: "employee-update-history-detail" */ '@/components/pages/employee/employee-update-history/detail/index'),
        props: true,
        meta: {
          menu: 'Detail Riwayat Pengkinian Data',
          type: 'view'
        }
      }
    ]
  },
  // DPLK ADMIN DASHBOARD //
  {
    path: '/',
    component: EmptyPageLayout,
    redirect: '/login',
    children: [
      {
        path: '/login',
        name: 'Login',
        component: Login,
        meta: {
          public: true,
          onlyWhenLoggedOut: true
        }
      },
      {
        path: '/reset-password',
        name: 'ResetPassword',
        component: () => import(/* webpackChunkName: "reset-password" */ '@/components/pages/ResetPassword'),
        meta: {
          public: true,
          onlyWhenLoggedOut: true
        }
      },
      {
        path: '/forgot-password',
        name: 'ForgotPassword',
        component: () => import(/* webpackChunkName: "forgot-password" */ '@/components/pages/ForgotPassword/index'),
        meta: {
          public: true,
          onlyWhenLoggedOut: true
        }
      },
      {
        path: '/activation',
        name: 'EmailActivation',
        component: () => import(/* webpackChunkName: "email-activation" */ '@/components/pages/email-activation/index'),
        meta: {
          public: true
        }
      },
      {
        path: '/invalid-activation',
        name: 'InvalidActivation',
        component: () => import(/* webpackChunkName: "invalid-activation" */ '@/components/pages/email-activation/InvalidActivation'),
        meta: {
          public: true
        }
      },
      {
        path: '/claim/detail/:token',
        name: 'ClaimDeepLink',
        component: () => import(/* webpackChunkName: "claim-deep-link" */ '@/components/pages/ClaimDeepLink')
      }
    ]
  },
  {
    path: '/admin',
    component: AdminLayout,
    redirect: () => getDefaultAuthenticatedRoute(getUserProfile()),
    children: [
      {
        path: 'dashboard',
        name: 'Dashboard',
        component: Dashboard,
        meta: {
          menu: 'Dashboard',
          type: 'view'
        }
      },
      // COMPANY DATA //
      {
        path: 'company-data',
        name: 'CompanyData',
        component: () => import( /* webpackChunkName: "company-data" */ '@/components/pages/admin/company-data/index'),
        beforeEnter: (to, from, next) => {
          const userProfile = getUserProfile()
          const roleName = userProfile?.role?.name?.toUpperCase()
          const companyId = userProfile?.user_employee?.company?.id

          if (roleName === 'COMPANY' && companyId) {
            next({ name: 'CompanyDataEdit', params: { id: companyId } })
            return
          }

          next()
        },
        meta: {
          menu: 'Data Perusahaan',
          type: 'view',
          apiURL: 'company'
        }
      },
      {
        path: 'company-data/create',
        name: 'CompanyDataCreate',
        component: () => import( /* webpackChunkName: "company-data-create" */ '@/components/pages/admin/company-data/create/index'),
        meta: {
          menu: 'Data Perusahaan',
          type: 'add',
          apiURL: 'company'
        }
      },
      {
        path: 'company-data/:id/edit',
        name: 'CompanyDataEdit',
        component: () => import( /* webpackChunkName: "company-data-edit" */ '@/components/pages/admin/company-data/edit/index'),
        props: true,
        meta: {
          menu: 'Data Perusahaan',
          type: 'edit',
          apiURL: 'company'
        }
      },
      {
        path: 'company-data/:id/detail',
        name: 'CompanyDataDetail',
        component: () => import( /* webpackChunkName: "company-data-detail" */ '@/components/pages/admin/company-data/detail/index'),
        props: true,
        meta: {
          menu: 'Data Perusahaan',
          type: 'view',
          apiURL: 'company'
        }
      },
      // EMPLOYEE DATA //
      {
        path: 'employee-data',
        name: 'Employee',
        component: () => import( /* webpackChunkName: "employee-data" */ '@/components/pages/admin/employee-data/index'),
        meta: {
          menu: 'Data Karyawan',
          type: 'view',
          apiURL: 'employee'
        }
      },
      {
        path: 'employee-data/create',
        name: 'EmployeeCreate',
        component: () => import( /* webpackChunkName: "employee-data-create" */ '@/components/pages/admin/employee-data/create/index'),
        meta: {
          menu: 'Data Karyawan',
          type: 'add',
          apiURL: 'employee'
        }
      },
      {
        path: 'employee-data/:id/detail',
        name: 'EmployeeDataDetail',
        component: () => import( /* webpackChunkName: "employee-data-detail" */ '@/components/pages/admin/employee-data/detail/index'),
        props: true,
        meta: {
          menu: 'Data Karyawan',
          type: 'view',
          apiURL: 'employee'
        }
      },
      {
        path: 'employee-data/:id/edit',
        name: 'EmployeeDataEdit',
        component: () => import( /* webpackChunkName: "employee-data-edit" */ '@/components/pages/admin/employee-data/edit/index'),
        props: true,
        meta: {
          menu: 'Data Karyawan',
          type: 'edit',
          apiURL: 'employee'
        }
      },
      {
        path: 'employee-data/pml-program',
        name: 'EmployeeDataPmlProgram',
        component: () => import( /* webpackChunkName: "employee-data-pml-program" */ '@/components/pages/admin/employee-data/pml-program/index'),
        props: true,
        meta: {
          menu: 'Data Karyawan',
          type: 'edit',
          apiURL: 'employee'
        }
      },
      {
        path: 'employee-data/existing-sync',
        name: 'EmployeeExistingSync',
        component: () => import( /* webpackChunkName: "employee-data-existing-sync" */ '@/components/pages/admin/employee-data/existing-sync/index'),
        meta: {
          menu: 'Data Karyawan',
          type: 'add',
          apiURL: 'employee'
        }
      },
      {
        path: 'employee-data/existing-sync/:id/detail',
        name: 'EmployeeExistingSyncDetail',
        props: true,
        component: () => import( /* webpackChunkName: "employee-data-existing-sync-detail" */ '@/components/pages/admin/employee-data/existing-sync/detail/index'),
        meta: {
          menu: 'Data Karyawan',
          type: 'add',
          apiURL: 'employee'
        }
      },
      {
        path: 'employee-data/bulk',
        name: 'EmployeeBulk',
        component: () => import( /* webpackChunkName: "employee-data-bulk" */ '@/components/pages/admin/employee-data/bulk/index'),
        meta: {
          menu: 'Data Karyawan',
          type: 'add',
          apiURL: 'employee'
        }
      },
      // Bulk Employee //
      {
        path: 'employee-data/bulk/:id/detail',
        name: 'EmployeeBulkDetail',
        props: true,
        component: () => import( /* webpackChunkName: "bulk-employee-detail" */ '@/components/pages/admin/employee-data/bulk/detail/index'),
        meta: {
          menu: 'Data Karyawan',
          type: 'add',
          apiURL: 'employee'
        }
      },
      // Bulk Iuran Employee //
      {
        path: 'employee-data/bulk/iuran/:id/detail',
        name: 'EmployeeBulkIuranDetail',
        props: true,
        component: () => import( /* webpackChunkName: "bulk-iuran-employee-detail" */ '@/components/pages/admin/employee-data/bulk/detail/iuran/index'),
        meta: {
          menu: 'Data Karyawan',
          type: 'add',
          apiURL: 'employee'
        }
      },
      // Bulk Pengalihan Employee //
      {
        path: 'employee-data/bulk/pengalihan/:id/detail',
        name: 'EmployeeBulkPengalihanDetail',
        props: true,
        component: () => import( /* webpackChunkName: "bulk-pengalihan-employee-detail" */ '@/components/pages/admin/employee-data/bulk/detail/pengalihan/index'),
        meta: {
          menu: 'Data Karyawan',
          type: 'add',
          apiURL: 'employee'
        }
      },
      // COMPANY SUBDIVISION //
      {
        path: 'company-subdivision',
        name: 'CompanySubdivision',
        component: () => import( /* webpackChunkName: "data-cabang" */ '@/components/pages/admin/company-subdivision/index'),
        meta: {
          menu: 'Data Cabang',
          type: 'view',
          apiURL: 'company_subdivision'
        }
      },
      {
        path: 'company-subdivision/bulk',
        name: 'CompanySubdivisionBulk',
        component: () => import( /* webpackChunkName: "company-subdivision-bulk" */ '@/components/pages/admin/company-subdivision/bulk/index'),
        meta: {
          menu: 'Data Cabang',
          type: 'add',
          apiURL: 'company_subdivision'
        }
      },
      {
        path: 'company-subdivision/bulk/:id/detail',
        name: 'CompanySubdivisionBulkDetail',
        props: true,
        component: () => import( /* webpackChunkName: "company-subdivision-bulk-detail" */ '@/components/pages/admin/company-subdivision/bulk/detail/index'),
        meta: {
          menu: 'Data Cabang',
          type: 'add',
          apiURL: 'company_subdivision'
        }
      },
      {
        path: 'company-subdivision/create',
        name: 'CompanySubdivisionCreate',
        component: () => import(/* webpackChunkName: "data-cabang-create" */ '@/components/pages/admin/company-subdivision/create/index'),
        meta: {
          menu: 'Data Cabang',
          type: 'add',
          apiURL: 'company_subdivision'
        }
      },
      {
        path: 'company-subdivision/:id/edit',
        name: 'CompanySubdivisionEdit',
        component: () => import(/* webpackChunkName: "data-cabang-edit" */ '@/components/pages/admin/company-subdivision/edit/index'),
        props: true,
        meta: {
          menu: 'Data Cabang',
          type: 'edit',
          apiURL: 'company_subdivision'
        }
      },
      {
        path: 'company-subdivision/:id/detail',
        name: 'CompanySubdivisionDetail',
        props: true,
        component: () => import(/* webpackChunkName: "data-cabang-detail" */ '@/components/pages/admin/company-subdivision/detail/index'),
        meta: {
          menu: 'Data Cabang',
          type: 'view',
          apiURL: 'company_subdivision'
        }
      },
      // UPDATED DATA //
      {
        path: 'updated-data',
        name: 'UpdatedData',
        component: () => import( /* webpackChunkName: "data-cabang" */ '@/components/pages/admin/updated/index'),
        meta: {
          menu: 'Pengkinian Data',
          type: 'view',
          apiURL: 'updating_data'
        }
      },

      {
        path: 'updated-data/:id/detail',
        name: 'UpdatedDataDetail',
        props: true,
        component: () => import( /* webpackChunkName: "employee-data-bulk-detail" */ '@/components/pages/admin/updated/detail/index'),
        meta: {
          menu: 'Pengkinian Data',
          type: 'view',
          apiURL: 'updating_data'
        }
      },
      // DATA CHANGES - BY EMPLOYEE
      {
        path: 'data-changes/by-employee',
        name: 'Oleh Karyawan',
        component: () => import(/* webpackChunkName: "data-changes-by-employee" */ '@/components/pages/admin/data-changes/employee/index'),
        meta: {
          menu: 'Pengkinian Data',
          type: 'view'
        }
      },
      {
        path: 'data-changes/by-employee/detail/:id',
        name: 'Detail Oleh Karyawan',
        component: () => import(/* webpackChunkName: "data-changes-by-employee" */ '@/components/pages/admin/data-changes/employee/detail/index'),
        meta: {
          menu: 'Pengkinian Data',
          type: 'view'
        }
      },
      // DATA CHANGES - BY COMPANY
      {
        path: 'data-changes/by-company',
        name: 'Oleh Perusahaan',
        component: () => import(/* webpackChunkName: "data-changes-by-company" */ '@/components/pages/admin/data-changes/company/index'),
        meta: {
          menu: 'Pengkinian Data',
          type: 'view'
        }
      },
      {
        path: 'data-changes/by-company/detail/:id',
        name: 'Detail Oleh Perusahaan',
        component: () => import(/* webpackChunkName: "data-changes-by-company" */ '@/components/pages/admin/data-changes/company/detail/index'),
        meta: {
          menu: 'Pengkinian Data',
          type: 'view'
        }
      },
      // INVOICE PAYMENT //
      {
        path: 'payment/invoice',
        name: 'InvoicePayment',
        component: () => import( /* webpackChunkName: "invoice-payment" */ '@/components/pages/admin/payment/invoice/index'),
        meta: {
          menu: 'Daftar Invoice',
          type: 'view',
          apiURL: 'invoice'
        }
      },
      {
        path: 'payment/invoice/create',
        name: 'InvoicePaymentCreate',
        component: () => import( /* webpackChunkName: "invoice-payment-create" */ '@/components/pages/admin/payment/invoice/create/index'),
        meta: {
          menu: 'Daftar Invoice',
          type: 'add',
          apiURL: 'invoice'
        }
      },
      {
        path: 'payment/invoice/:id/detail',
        name: 'InvoicePaymentDetail',
        component: () => import( /* webpackChunkName: "invoice-payment-detail" */ '@/components/pages/admin/payment/invoice/detail/index'),
        props: true,
        meta: {
          menu: 'Daftar Invoice',
          type: 'view',
          apiURL: 'invoice'
        }
      },
      {
        path: 'payment/invoice/adhoc',
        name: 'InvoicePaymentAdhoc',
        component: () => import( /* webpackChunkName: "invoice-payment-adhoc" */ '@/components/pages/admin/payment/invoice/adhoc/index'),
        meta: {
          menu: 'Daftar Invoice',
          type: 'add',
          apiURL: 'invoice'
        }
      },
      // PAYMENT CONFIRMATION //
      {
        path: 'payment/payment-confirmation',
        name: 'PaymentConfirmation',
        component: () => import( /* webpackChunkName: "payment-confirmation" */ '@/components/pages/admin/payment/payment-confirmation/index'),
        meta: {
          menu: 'Konfirmasi Pembayaran',
          type: 'view',
          apiURL: 'confirm'
        }
      },
      {
        path: 'payment/payment-confirmation/:id/detail',
        name: 'PaymentConfirmationDetail',
        component: () => import( /* webpackChunkName: "payment-confirmation-detail" */ '@/components/pages/admin/payment/payment-confirmation/detail/index'),
        props: true,
        meta: {
          menu: 'Konfirmasi Pembayaran',
          type: 'view'
        }
      },
      // JOURNAL CONTRIBUTION //
      {
        path: 'payment/journal-contribution',
        name: 'JournalContribution',
        component: () => import( /* webpackChunkName: "journal-contribution" */ '@/components/pages/admin/payment/journal-contribution/index'),
        meta: {
          menu: 'Jurnal Iuran',
          type: 'view',
          apiURL: 'journal'
        }
      },
      // PAYMENT COMPANY //
      {
        path: 'payment-company',
        name: 'PaymentCompany',
        component: () => import( /* webpackChunkName: "payment-company" */ '@/components/pages/admin/payment-company/index'),
        meta: {
          menu: 'Riwayat Pembayaran',
          type: 'view',
          apiURL: 'payment'
        }
      },
      {
        path: 'payment-company/:id/detail',
        name: 'PaymentCompanyDetail',
        component: () => import( /* webpackChunkName: "payment-company-detail" */ '@/components/pages/admin/payment-company/detail/index'),
        props: true,
        meta: {
          menu: 'Riwayat Pembayaran',
          type: 'view',
          apiURL: 'payment'
        }
      },
      // CLAIM
      {
        path: 'claim',
        name: 'Claim',
        component: () => import(/* webpackChunkName: "claim" */ '@/components/pages/admin/claim/index'),
        meta: {
          menu: 'Klaim',
          type: 'view',
          apiURL: 'claim'
        }
      },
      {
        path: 'claim/create',
        name: 'ClaimCreate',
        component: () => import( /* webpackChunkName: "claim-create" */ '@/components/pages/admin/claim/create/index'),
        meta: {
          menu: 'Klaim',
          type: 'add',
          apiURL: 'claim'
        }
      },
      {
        path: 'claim/:id/detail',
        name: 'ClaimDetail',
        component: () => import(/* webpackChunkName: "claim-detail" */ '@/components/pages/admin/claim/detail/index'),
        props: true,
        meta: {
          menu: 'Klaim',
          type: 'view',
          apiURL: 'claim'
        }
      },
      // LAPORAN POSISI DANA //
      {
        path: 'lpd-company',
        name: 'LpdCompany',
        component: () => import(/* webpackChunkName: "lpd-company" */ '@/components/pages/admin/lpd-company/index'),
        meta: {
          menu: 'Laporan Posisi Dana',
          type: 'view',
          apiURL: 'company'
        }
      },
      {
        path: 'lpd-company/:id',
        name: 'LpdCompanyDetail',
        component: () => import(/* webpackChunkName: "lpd-company-detail" */ '@/components/pages/admin/lpd-company/detail/index'),
        props: true,
        meta: {
          menu: 'Laporan Posisi Dana',
          type: 'view',
          apiURL: 'company'
        }
      },
      // REPORTS //
      {
        path: 'reports',
        name: 'Reports',
        component: () => import(/* webpackChunkName: "reports" */ '@/components/pages/admin/reports/index'),
        meta: {
          menu: 'Laporan',
          type: 'view',
          apiURL: 'report'
        }
      },
      {
        path: 'reports/create',
        name: 'ReportsCreate',
        component: () => import(/* webpackChunkName: "reports-create" */ '@/components/pages/admin/reports/create/index'),
        meta: {
          menu: 'Laporan',
          type: 'add',
          apiURL: 'report'
        }
      },
      {
        path: 'reports/lpd',
        name: 'LpdReports',
        component: () => import(/* webpackChunkName: "lpd-reports" */ '@/components/pages/admin/reports/lpd/index'),
        meta: {
          menu: 'Laporan Posisi Dana',
          type: 'view'
        }
      },
      // FORM //
      {
        path: 'forms',
        name: 'Forms',
        component: () => import(/* webpackChunkName: "forms" */ '@/components/pages/admin/forms/index'),
        meta: {
          menu: 'Formulir',
          type: 'view',
          apiURL: 'form'
        }
      },
      {
        path: 'forms/create',
        name: 'FormsCreate',
        component: () => import(/* webpackChunkName: "forms-create" */ '@/components/pages/admin/forms/create/index'),
        meta: {
          menu: 'Formulir',
          type: 'add',
          apiURL: 'form'
        }
      },
      {
        path: 'forms/:id/edit',
        name: 'FormsEdit',
        props: true,
        component: () => import(/* webpackChunkName: "forms-edit" */ '@/components/pages/admin/forms/edit/index'),
        meta: {
          menu: 'Formulir',
          type: 'edit',
          apiURL: 'form'
        }
      },
      // MANAGEMENT - USER Company //
      {
        path: 'user-company',
        name: 'UserCompany',
        component: () => import(/* webpackChunkName: "user-company" */ '@/components/pages/admin/userCompany/index'),
        meta: {
          menu: 'UserCompany',
          type: 'view'
        }
      },
      {
        path: 'user-company/:id/edit',
        name: 'UserCompanyEdit',
        props: true,
        component: () => import(/* webpackChunkName: "user-company" */ '@/components/pages/admin/userCompany/edit/index'),
        meta: {
          menu: 'UserCompany',
          type: 'edit'
        }
      },
      {
        path: 'user-company/:id/detail',
        name: 'UserCompanyDetail',
        props: true,
        component: () => import(/* webpackChunkName: "user-company" */ '@/components/pages/admin/userCompany/detail/index'),
        meta: {
          menu: 'UserCompany',
          type: 'view'
        }
      },
      // MANAGEMENT - TYPE OF INVESTMENT //
      {
        path: 'type-of-investment',
        name: 'TypeOfInvestment',
        component: () => import(/* webpackChunkName: "type-of-investment" */ '@/components/pages/admin/management-account/type-investment/index'),
        meta: {
          menu: 'Type of Investment',
          type: 'view',
          apiURL: 'investment_category'
        }
      },
      // MANAGEMENT - ROLE //
      {
        path: 'role',
        name: 'Role',
        component: () => import(/* webpackChunkName: "role" */ '@/components/pages/admin/management-account/role/index'),
        meta: {
          menu: 'Role',
          type: 'view',
          apiURL: 'access'
        }
      },
      {
        path: 'role/create',
        name: 'RoleCreate',
        component: () => import(/* webpackChunkName: "role-create" */ '@/components/pages/admin/management-account/role/create/index'),
        meta: {
          menu: 'Role',
          type: 'add',
          apiURL: 'access'
        }
      },
      {
        path: 'role/:id/edit',
        name: 'RoleEdit',
        props: true,
        component: () => import(/* webpackChunkName: "role-edit" */ '@/components/pages/admin/management-account/role/edit/index'),
        meta: {
          menu: 'Role',
          type: 'edit',
          apiURL: 'access'
        }
      },
      {
        path: 'role/:id/detail',
        name: 'RoleDetail',
        props: true,
        component: () => import(/* webpackChunkName: "role-detail" */ '@/components/pages/admin/management-account/role/detail/index'),
        meta: {
          menu: 'Role',
          type: 'view',
          apiURL: 'access'
        }
      },
      // MANAGEMENT - USER //
      {
        path: 'user',
        name: 'User',
        component: () => import(/* webpackChunkName: "user" */ '@/components/pages/admin/management-account/user/index'),
        meta: {
          menu: 'User',
          type: 'view',
          apiURL: 'account'
        }
      },
      {
        path: 'user/create',
        name: 'UserCreate',
        component: () => import(/* webpackChunkName: "user-create" */ '@/components/pages/admin/management-account/user/create/index'),
        meta: {
          menu: 'User',
          type: 'add',
          apiURL: 'account'
        }
      },
      {
        path: 'user/:id/edit',
        name: 'UserEdit',
        props: true,
        component: () => import(/* webpackChunkName: "user-edit" */ '@/components/pages/admin/management-account/user/edit/index'),
        meta: {
          menu: 'User',
          type: 'edit',
          apiURL: 'account'
        }
      },
      {
        path: 'user/:id/detail',
        name: 'UserDetail',
        props: true,
        component: () => import(/* webpackChunkName: "user-detail" */ '@/components/pages/admin/management-account/user/detail/index'),
        meta: {
          menu: 'User',
          type: 'view',
          apiURL: 'account'
        }
      },
      // LOG ACTIVITY //
      {
        path: 'log-activity',
        name: 'LogActivity',
        component: () => import(/* webpackChunkName: "log-activity" */ '@/components/pages/admin/log-activity')
      },
      // NOTIFICATION PAYMENT //
      {
        path: 'notification-payment',
        name: 'NotificationPayment',
        component: () => import(/* webpackChunkName: "notification-payment" */ '@/components/pages/admin/notification-payment'),
        meta: {
          menu: 'NotificationPayment',
          type: 'view',
          apiURL: 'notification-payment'
        }
      },
      {
        path: 'notification-payment/create',
        name: 'NotificationCreate',
        component: () => import(/* webpackChunkName: "notification-payment-create" */ '@/components/pages/admin/notification-payment/create'),
        meta: {
          menu: 'NotificationPayment',
          type: 'add',
          apiURL: 'notification-payment'
        }
      },
      // LOG NOTIFICATION //
      {
        path: 'log-notification-email',
        name: 'LogNotificationEmail',
        component: () => import(/* webpackChunkName: "log-notification-email" */ '@/components/pages/admin/log-notification-email'),
        meta: {
          menu: 'Log Notifikasi Email',
          type: 'view',
          apiURL: 'log-notification-email'
        }
      },
      // EMAIL BROADCAST //
      {
        path: 'email-broadcast',
        name: 'EmailBroadcast',
        component: () => import(/* webpackChunkName: "email-broadcast" */ '@/components/pages/admin/email-broadcast'),
        meta: {
          menu: 'Email Broadcast',
          type: 'view',
          apiURL: 'email-broadcast'
        }
      },
      {
        path: 'email-broadcast/create',
        name: 'EmailBroadcastCreate',
        component: () => import(/* webpackChunkName: "email-broadcast-create" */ '@/components/pages/admin/email-broadcast/create'),
        meta: {
          menu: 'Email Broadcast',
          type: 'add',
          apiURL: 'email-broadcast'
        }
      },
      {
        path: 'email-broadcast/:id/edit',
        name: 'EmailBroadcastEdit',
        props: true,
        component: () => import(/* webpackChunkName: "email-broadcast-edit" */ '@/components/pages/admin/email-broadcast/edit'),
        meta: {
          menu: 'Email Broadcast',
          type: 'edit',
          apiURL: 'email-broadcast'
        }
      },
      // CONFIGURATION RETIREMENT //
      {
        path: 'configuration',
        name: 'ConfigurationRetirement',
        component: () => import(/* webpackChunkName: "configuration-retirement" */ '@/components/pages/admin/configuration'),
        meta: {
          menu: 'Konfigurasi',
          type: 'view',
          apiURL: 'configuration'
        }
      },
      // LOG SYNC //
      {
        path: 'log-sync',
        name: 'LogSync',
        component: () => import(/* webpackChunkName: "log-sync" */ '@/components/pages/admin/log-sync'),
        meta: {
          menu: 'Log Sinkronisasi',
          type: 'view',
          apiURL: 'log-sync'
        }
      },
      // CORE SYNC PROGRESS //
      {
        path: 'core-sync-progress',
        name: 'CoreSyncProgress',
        component: () => import(/* webpackChunkName: "core-sync-progress" */ '@/components/pages/admin/core-sync-progress'),
        meta: {
          menu: 'Progres Sinkronisasi Core',
          type: 'view',
          apiURL: 'core-sync-progress'
        }
      },
      // CORE MAPPING //
      {
        path: 'core-mapping',
        name: 'CoreMapping',
        component: () => import(/* webpackChunkName: "core-mapping" */ '@/components/pages/admin/core-mapping'),
        meta: {
          menu: 'Mapping Core',
          type: 'view',
          apiURL: 'company'
        }
      },
      // TERMS & CONDITION //
      {
        path: 'terms-condition',
        name: 'TermsCondition',
        component: () => import(/* webpackChunkName: "terms-condition" */ '@/components/pages/admin/terms-condition'),
        meta: {
          menu: 'Syarat dan Ketentuan',
          type: 'view',
          apiURL: 'terms-condition'
        }
      },
      // CHANGE PASSWORD //
      {
        path: 'change-password',
        name: 'Change Password',
        component: () => import(/* webpackChunkName: "change-password" */ '@/components/pages/admin/change-password')
      },
      {
        path: 'unauthorized',
        name: 'Unauthorized',
        component: () => import(/*webpackChunkName: "unauthorized" */ '@/components/pages/Unauthorized')
      }
    ]
  },
  {
    path: '/:catchAll(.*)',
    name: 'NotFound',
    component: () => import(/* webpackChunkName: "not-found" */ '@/components/pages/NotFound'),
    meta: {
      public: true
    }
  }
]

const router = createRouter({
  history: createWebHistory(''),
  routes
})

export const handleChunkLoadError = (error, to) => {
  if (isStaleChunkError(error)) {
    window.location.href = to.fullPath
  }
}

router.onError(handleChunkLoadError)

export const getDefaultAuthenticatedRoute = (userProfile, permissions) => {
  const roleName = userProfile?.role?.name?.toUpperCase()
  if (roleName === 'USER') return '/employee-dashboard/employee-information'
  if (roleName === 'SUPER ADMIN') return '/admin/dashboard'

  const canView = (menu) => permissions
    ? permissions.some(item => item?.menu?.toUpperCase() === menu?.toUpperCase() && item.view === true)
    : checkRoleAccess('view', menu)
  const environment = process.env.VUE_APP_ENV === 'prod' ? 'production' : process.env.VUE_APP_ENV

  for (const item of sidebar) {
    if (environment && !item.showOn?.includes(environment)) continue
    for (const entry of item.child?.length ? item.child : [item]) {
      if (!entry.to) continue
      const route = router.resolve(entry.to)
      if (!route.meta?.menu || !canView(route.meta.menu)) continue
      const companyId = userProfile?.user_employee?.company?.id
      if (entry.to === '/admin/company-data' && roleName === 'COMPANY' && companyId) {
        const action = permissions
          ? permissions.some(permission => permission?.menu?.toUpperCase() === 'DATA PERUSAHAAN' && permission.edit === true)
          : checkRoleAccess('edit', 'Data Perusahaan')
        return `/admin/company-data/${encodeURIComponent(companyId)}/${action ? 'edit' : 'detail'}`
      }
      return entry.to
    }
  }

  return '/admin/unauthorized'
}

export const canAccessRoute = (to, userProfile) => {
  if (!to.meta?.menu) return true
  if (userProfile?.role?.name?.toUpperCase() === 'SUPER ADMIN') return true

  const accessType = to.meta?.type || 'view'
  return checkRoleAccess(accessType, to.meta.menu)
}

export const buildNavigationDecision = (to, loggedIn, userProfile) => {
  const isPublic = to.matched.some(record => record.meta.public)
  const onlyWhenLoggedOut = to.matched.some(record => record.meta.onlyWhenLoggedOut)

  if (!isPublic && !loggedIn) {
    return {
      path:'/login',
      query: {redirect: to.fullPath}  // Store the full path to redirect the user to after login
    }
  }

  // Do not allow user to visit login page or register page if they are logged in
  if (loggedIn && onlyWhenLoggedOut) {
    return getDefaultAuthenticatedRoute(userProfile)
  }

  if (loggedIn && !canAccessRoute(to, userProfile)) {
    return to.meta?.menu === 'Laporan Posisi Dana'
      ? '/admin/unauthorized?reason=lpd'
      : '/admin/unauthorized'
  }

  return true
}

router.beforeEach((to, from, next) => {
  const loggedIn = isLogin()
  const userProfile = getUserProfile()
  const navigationDecision = buildNavigationDecision(to, loggedIn, userProfile)

  if (navigationDecision === true) {
    next()
    return
  }

  next(navigationDecision)
})

export default router
